import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CheckCircle, Loader } from "lucide-react";
import { useSubscriptionState } from "@/zustand/SubscriptionState";
import {
  fetchCheckoutSession,
  fetchSubscription,
} from "@/server/services/stripeService";
import { Button } from "@/components/primitives/button";
import { queryClient } from "@/queryClient";
import { QUERY_KEYS } from "@/config/queryKeys";
import { CheckoutSessionDetails } from "@/server/types/subscription";
import { formatPrice } from "@/config/subscriptionPlans";

const REDIRECT_COUNTDOWN_S = 5;

/**
 * Landing page after a successful Stripe checkout redirect.
 * Fetches session details for the confirmation display and the full
 * subscription object to update the app state, then auto-redirects.
 */
export default function SubscriptionSuccessPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const setSubscription = useSubscriptionState((s) => s.setSubscription);

  const [sessionDetails, setSessionDetails] =
    useState<CheckoutSessionDetails | null>(null);
  const [fetchFailed, setFetchFailed] = useState(false);
  const [countdown, setCountdown] = useState(REDIRECT_COUNTDOWN_S);

  const chatRoute = "/";
  const sessionId = searchParams.get("session_id");

  // Guard: no session_id → redirect immediately
  useEffect(() => {
    if (!sessionId) {
      navigate(chatRoute, { replace: true });
    }
  }, [sessionId, chatRoute, navigate]);

  // Fetch session details + full subscription in parallel
  useEffect(() => {
    if (!sessionId) {
      return;
    }

    let cancelled = false;

    const load = async () => {
      const [detailsResult, subResult] = await Promise.allSettled([
        fetchCheckoutSession(sessionId),
        fetchSubscription(),
      ]);
      if (cancelled) {
        return;
      }
      if (detailsResult.status === "fulfilled") {
        setSessionDetails(detailsResult.value);
      }
      if (subResult.status === "fulfilled") {
        setSubscription(subResult.value);
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.stripeInvoices });
      }
      if (
        detailsResult.status === "rejected" &&
        subResult.status === "rejected"
      ) {
        setFetchFailed(true);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [sessionId, setSubscription]);

  // Countdown + auto-redirect (starts once data is ready or fetch failed)
  const countdownStarted = sessionDetails !== null || fetchFailed;
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!countdownStarted) {
      return;
    }

    countdownRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(countdownRef.current!);
          navigate(chatRoute, { replace: true });
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
      }
    };
  }, [countdownStarted, chatRoute, navigate, t]);

  const handleGoNow = () => {
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
    }
    navigate(chatRoute, { replace: true });
  };

  if (!sessionId) {
    return null;
  }

  // Loading state
  if (!sessionDetails && !fetchFailed) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-background text-foreground">
        <div className="flex flex-col items-center gap-3 rounded-md bg-card p-8">
          <Loader className="size-8 animate-spin text-primary" />
          <p className="text-base font-medium">
            {t("subscription.processingSubscription")}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen w-full items-center justify-center bg-background text-foreground">
      <div className="flex w-full max-w-sm flex-col items-center gap-4 rounded-xl bg-card p-8 shadow-md">
        <CheckCircle className="size-12 text-green-500" />

        {sessionDetails ? (
          <>
            <h1 className="text-xl font-semibold">
              {t("subscription.subscriptionActivated")}
            </h1>
            <div className="w-full rounded-lg bg-muted/50 px-4 py-3 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">
                  {t("subscription.plan")}
                </span>
                <span className="font-medium capitalize">
                  {sessionDetails.plan}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">
                  {t("subscription.billing")}
                </span>
                <span className="font-medium capitalize">
                  {sessionDetails.interval === "month"
                    ? t("subscription.monthly")
                    : t("subscription.yearly")}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">
                  {t("subscription.charged")}
                </span>
                <span className="font-medium">
                  {formatPrice(sessionDetails.amount / 100)}{" "}
                  {sessionDetails.currency.toUpperCase()}
                </span>
              </div>
              {sessionDetails.email && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">
                    {t("subscription.receiptSentTo")}
                  </span>
                  <span className="font-medium">{sessionDetails.email}</span>
                </div>
              )}
            </div>
          </>
        ) : (
          <>
            <h1 className="text-xl font-semibold">
              {t("subscription.paymentReceived")}
            </h1>
            <p className="text-center text-sm text-muted-foreground">
              {t("subscription.checkEmail")}
            </p>
          </>
        )}

        <Button className="w-full" onClick={handleGoNow}>
          {t("subscription.goToApp")} ({countdown}s)
        </Button>
      </div>
    </div>
  );
}
